import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Badge, Card, DataTable, IdCell, Loader, PageHead, StatCard, inr, num, timeAgo, titleCase,
} from '../components/ui';
import { dashboardApi } from '../api';
import { useToast } from '../context/ToastContext';

const ROLE_COLORS = {
  buyer: '#00A651',
  manufacturer: '#0B8A3D',
  distributor: '#2FC46F',
  supplier: '#6BD69B',
  logistics: '#07682E',
  insurance: '#A6E6C2',
  finance: '#094F26',
  superadmin: '#23392E',
};

const ChartTip = ({ active, payload, label, money }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: '9px 12px',
        boxShadow: 'var(--shadow-md)',
        fontSize: 12.5,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {titleCase(p.dataKey)}: <strong>{money && p.dataKey === 'revenue' ? inr(p.value) : num(p.value)}</strong>
        </div>
      ))}
    </div>
  );
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    dashboardApi
      .overview()
      .then(res => setData(res.data))
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loader label="Crunching platform numbers…" />;
  if (!data) return null;

  const c = data.cards;
  const roleData = Object.entries(data.usersByRole || {})
    .filter(([, count]) => count > 0)
    .map(([role, count]) => ({ name: titleCase(role), value: count, role }));

  return (
    <>
      <PageHead
        title="Platform overview"
        subtitle="Everything moving across the Tarpex network, right now."
      />

      <div className="stat-grid stagger">
        <StatCard accent label="Gross merchandise value" value={inr(c.gmv, true)} icon="₹" foot={`${num(c.totalOrders)} orders lifetime`} />
        <StatCard label="Commission earned" value={inr(c.commissionEarned, true)} icon="◈" foot="Platform take" />
        <StatCard label="This month" value={inr(c.monthGmv, true)} icon="📈" foot={`${num(c.monthOrders)} orders`} />
        <StatCard label="Orders today" value={num(c.todayOrders)} icon="🛒" foot="Since midnight" onClick={() => navigate('/orders')} />
        <StatCard label="Companies" value={num(c.totalCompanies)} icon="⬢" foot={`${num(c.totalUsers)} user accounts`} onClick={() => navigate('/companies')} />
        <StatCard
          label="Pending verification"
          value={num(c.pendingVerification)}
          icon="⏳"
          foot={c.pendingVerification ? 'Action required' : 'All clear'}
          onClick={() => navigate('/companies')}
        />
        <StatCard label="Live products" value={num(c.totalProducts)} icon="▦" onClick={() => navigate('/products')} />
        <StatCard label="Payments collected" value={inr(c.totalCollected, true)} icon="✓" onClick={() => navigate('/payments')} />
        <StatCard label="Open claims" value={num(c.openClaims)} icon="⚠" onClick={() => navigate('/claims')} />
        <StatCard label="Active contracts" value={num(c.activeContracts)} icon="📄" onClick={() => navigate('/contracts')} />
      </div>

      <div className="mt-3" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2.1fr) minmax(0, 1fr)', gap: 14 }}>
        <Card title="Revenue & orders" subtitle="Last 30 days">
          <ResponsiveContainer width="100%" height={286}>
            <AreaChart data={data.revenueSeries} margin={{ top: 6, right: 6, left: -14, bottom: 0 }}>
              <defs>
                <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00A651" stopOpacity={0.32} />
                  <stop offset="100%" stopColor="#00A651" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={26} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={false} tickLine={false} tickFormatter={v => inr(v, true)} width={62} />
              <Tooltip content={<ChartTip money />} />
              <Area type="monotone" dataKey="revenue" stroke="#00A651" strokeWidth={2.4} fill="url(#revFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Network composition" subtitle="Accounts by role">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={roleData} dataKey="value" nameKey="name" innerRadius={52} outerRadius={80} paddingAngle={2.5} stroke="none">
                {roleData.map(entry => (
                  <Cell key={entry.role} fill={ROLE_COLORS[entry.role] || '#6BD69B'} />
                ))}
              </Pie>
              <Tooltip content={<ChartTip />} />
            </PieChart>
          </ResponsiveContainer>

          <div className="col" style={{ gap: 6, marginTop: 6 }}>
            {roleData.map(entry => (
              <div className="row-between text-sm" key={entry.role}>
                <span className="row" style={{ gap: 7 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: ROLE_COLORS[entry.role] || '#6BD69B' }} />
                  {entry.name}
                </span>
                <strong>{num(entry.value)}</strong>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-3" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 1fr)', gap: 14 }}>
        <Card title="Recent orders" subtitle="Across every seller on the network" bodyClass="" style={{ overflow: 'hidden' }}>
          <DataTable
            columns={[
              {
                key: 'orderNumber',
                label: 'Order',
                render: row => (
                  <>
                    <div className="cell-strong mono">{row.orderNumber}</div>
                    <div className="cell-muted">{timeAgo(row.createdAt)}</div>
                  </>
                ),
              },
              {
                key: 'buyer',
                label: 'Buyer → Seller',
                render: row => (
                  <>
                    <div>{row.buyer?.name || '—'}</div>
                    <div className="cell-muted">→ {row.seller?.name || '—'}</div>
                  </>
                ),
              },
              { key: 'grandTotal', label: 'Value', align: 'right', render: row => <strong>{inr(row.grandTotal)}</strong> },
              { key: 'status', label: 'Status', render: row => <Badge status={row.status} /> },
            ]}
            rows={data.recentOrders}
            onRowClick={row => navigate(`/admin/orders/${row._id}`)}
          />
        </Card>

        <Card title="Top manufacturers" subtitle="By lifetime business">
          {data.topSellers.length === 0 ? (
            <p className="text-muted text-sm">No sales recorded yet.</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={168}>
                <BarChart data={data.topSellers.map(s => ({ name: s.name.split(' ')[0], value: s.totalBusiness }))} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={false} tickLine={false} tickFormatter={v => inr(v, true)} width={58} />
                  <Tooltip content={<ChartTip money />} cursor={{ fill: 'var(--green-50)' }} />
                  <Bar dataKey="value" fill="#00A651" radius={[6, 6, 0, 0]} maxBarSize={38} />
                </BarChart>
              </ResponsiveContainer>

              <div className="divider" />

              <div className="col" style={{ gap: 12 }}>
                {data.topSellers.map(seller => (
                  <div className="row-between" key={seller._id}>
                    <IdCell image={seller.logo} name={seller.name} sub={`${seller.code} · ${num(seller.totalOrders)} orders`} size="sm" />
                    <strong className="text-sm">{inr(seller.totalBusiness, true)}</strong>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>
    </>
  );
};

export default Dashboard;
